import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Table, Input, Icon, Select } from "antd";
import { PORTFOLIO_CASE_COLUMNS } from "../constants";
import FiMovementBar, {
    canActionFiMovement,
    FI_MOVEMENT_SELECTABLE_STATUSES
} from "../../fi-movement-bar";

// Same case-status options the standalone All Cases tab filters on.
const CASE_STATUS_OPTIONS = ["Active", "Closed", "Open", "Rejected", "Hold"];

const DASH = "—";

function toNumber(value) {
    const n = Number(String(value == null ? "" : value).replace(/[^0-9.-]/g, ""));
    return isFinite(n) && !Number.isNaN(n) ? n : NaN;
}

// Extracts the numeric case id from either the row's own `id` field or from
// the display string ("PGCP-160522" → 160522). Returns null when nothing
// numeric is on record.
function numericCaseId(value, row) {
    const raw =
        (row && (row.id || row.case_id || row.caseId_numeric)) ||
        (typeof value === "string" && value.match(/(\d+)/)
            ? value.match(/(\d+)/)[1]
            : null);
    const num = Number(raw);
    return Number.isFinite(num) && num > 0 ? num : null;
}

// URL for a case's Credit Checker view. Reuses the current pageID from the
// URL (falls back to `companies` for direct-access flows).
function caseUrl(id) {
    const parts = window.location.pathname.split("/");
    const pageID = parts[2] || "companies";
    return `${window.location.origin}/app/${pageID}/${id}/credit_checker`;
}

function renderCell(value, col, width, row) {
    if (value === null || value === undefined || value === "") {
        return <span className={"cc-pf-empty"}>{DASH}</span>;
    }
    // Case ID cell links to the target case's Credit Checker in a new tab.
    if (col.key === "caseId") {
        const id = numericCaseId(value, row);
        if (id) {
            return (
                <a
                    className={"cc-pf-caselink"}
                    href={caseUrl(id)}
                    target={"_blank"}
                    rel={"noreferrer noopener"}
                    title={`Open ${value} in a new tab`}
                >
                    {String(value)}
                </a>
            );
        }
    }
    if (col.expiry) {
        const status = row && row.expiry_status;
        if (status === "expired" || status === "expiring") {
            return (
                <span className={"cc-pf-expiry"}>
                    <span>{String(value)}</span>
                    <span
                        className={"cc-pf-expchip cc-pf-expchip--" + status}
                    >
                        {status === "expired" ? "Expired" : "Expiring soon"}
                    </span>
                </span>
            );
        }
        return String(value);
    }
    if (col.money) {
        const n = toNumber(value);
        return Number.isNaN(n)
            ? String(value)
            : "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    }
    if (col.number) {
        const n = toNumber(value);
        return Number.isNaN(n)
            ? String(value)
            : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    }
    if (col.bool) {
        const truthy =
            value === true ||
            value === 1 ||
            ["true", "yes", "1"].includes(String(value).toLowerCase());
        return truthy ? "Yes" : "No";
    }
    if (col.status) {
        const v = String(value).toLowerCase();
        const tone = ["closed", "rejected", "hold"].some(s => v.indexOf(s) !== -1)
            ? "bad"
            : ["active", "open"].some(s => v.indexOf(s) !== -1)
                ? "good"
                : "muted";
        return (
            <span className={"cc-pf-status cc-pf-status--" + tone}>{value}</span>
        );
    }
    // Long text (seller / anchor names): truncate with an EXPLICIT pixel width
    // (antd `ellipsis` doesn't truncate fixed/frozen columns), full value on
    // hover. ~18px reserved for cell padding.
    return (
        <span
            className={"cc-pf-trunc"}
            style={{ maxWidth: Math.max((width || 150) - 18, 40) }}
            title={String(value)}
        >
            {String(value)}
        </span>
    );
}

function columnWidth(col, idx) {
    if (idx === 0) {
        return 130;
    }
    if (col.status) {
        return 110;
    }
    // Date + an "Expiring soon" / "Expired" chip need a little extra room.
    if (col.expiry) {
        return 180;
    }
    // Seller / Anchor names are long — give them room (still ellipsis-clipped).
    if (col.key === "sellers" || col.key === "anchors") {
        return 200;
    }
    if (col.money) {
        return 150;
    }
    if (col.number) {
        return 130;
    }
    return 150;
}

// Dates are rendered DD/MM/YYYY (see buildPortfolioCases in index.js). A plain
// string compare on that format sorts wrong (day comes before year), so parse
// via moment and compare epoch ms. Nulls sort as 0 (top on ascend).
function dateToMillis(value) {
    if (value === null || value === undefined || value === "") {
        return 0;
    }
    const m = moment(String(value), ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601], true);
    return m.isValid() ? m.valueOf() : 0;
}

function sorter(col) {
    return (a, b) => {
        if (col.money || col.number) {
            const na = toNumber(a[col.key]);
            const nb = toNumber(b[col.key]);
            if (Number.isNaN(na) && Number.isNaN(nb)) {
                return 0;
            }
            if (Number.isNaN(na)) {
                return -1;
            }
            if (Number.isNaN(nb)) {
                return 1;
            }
            return na - nb;
        }
        if (col.date || col.expiry) {
            return dateToMillis(a[col.key]) - dateToMillis(b[col.key]);
        }
        return String(a[col.key] == null ? "" : a[col.key])
            .toLowerCase()
            .localeCompare(
                String(b[col.key] == null ? "" : b[col.key]).toLowerCase()
            );
    };
}

/**
 * Detailed All-Cases grid: every account-summary parameter per case, with
 * per-column sorting, a free-text filter, and the identity columns (through
 * Anchor) frozen while the rest scroll horizontally.
 */
class PortfolioCasesTable extends React.Component {
    state = { query: "", statusFilter: [], selectedRowKeys: [], selectedRows: [] };

    onSearch = e => this.setState({ query: e.target.value });

    onStatusChange = statusFilter => this.setState({ statusFilter });

    onSelectionChange = (selectedRowKeys, selectedRows) =>
        this.setState({ selectedRowKeys, selectedRows });

    // A row can only take part in an FI movement when the viewer has the
    // action permission (and isn't in the recredit-view-only role) AND the
    // case itself is Active/Open — matches the standalone All Cases tab.
    fiMovementCheckboxProps = record => ({
        disabled:
            !canActionFiMovement(this.props.permissionContext) ||
            Boolean(this.props.recreditViewOnlyPermission) ||
            !FI_MOVEMENT_SELECTABLE_STATUSES.includes(record.case_status)
    });

    onFiMovementDone = () => {
        this.setState({ selectedRowKeys: [], selectedRows: [] });
        if (this.props.onFiMovementDone) {
            this.props.onFiMovementDone();
        }
    };

    filteredRows() {
        const { rows } = this.props;
        const q = this.state.query.trim().toLowerCase();
        const statuses = this.state.statusFilter;
        let out = rows || [];
        // Case-status filter — matches the All Cases tab (on case_status_display,
        // which is the grid's `case_status` field).
        if (statuses && statuses.length) {
            out = out.filter(row => statuses.includes(row.case_status));
        }
        if (q) {
            out = out.filter(row =>
                PORTFOLIO_CASE_COLUMNS.some(
                    col =>
                        String(row[col.key] == null ? "" : row[col.key])
                            .toLowerCase()
                            .indexOf(q) !== -1
                )
            );
        }
        return out;
    }

    render() {
        // Freeze the first two columns (Case ID + Case Status) only.
        const freezeUntil = 1;
        const columns = PORTFOLIO_CASE_COLUMNS.map((col, idx) => {
            const width = columnWidth(col, idx);
            return {
                title: col.label,
                dataIndex: col.key,
                key: col.key,
                fixed: idx <= freezeUntil ? "left" : undefined,
                width,
                align: col.money || col.number ? "right" : "left",
                sorter: sorter(col),
                render: (value, row) => renderCell(value, col, width, row)
            };
        });
        const totalX = PORTFOLIO_CASE_COLUMNS.reduce(
            (acc, col, idx) => acc + columnWidth(col, idx),
            0
        );
        const rows = this.filteredRows();

        return (
            <div className={"cc-pf-grid"}>
                <div className={"cc-pf-grid__bar"}>
                    <span className={"cc-pf-search"}>
                        <Icon type={"search"} />
                        <Input
                            value={this.state.query}
                            onChange={this.onSearch}
                            placeholder={"Filter cases…"}
                            allowClear
                        />
                    </span>
                    <span className={"cc-pf-grid__right"}>
                        <Select
                            mode={"multiple"}
                            allowClear
                            className={"cc-pf-statusfilter"}
                            style={{ minWidth: 200 }}
                            placeholder={"Case Status"}
                            value={this.state.statusFilter}
                            onChange={this.onStatusChange}
                            maxTagCount={2}
                            getPopupContainer={trigger => trigger.parentNode}
                        >
                            {CASE_STATUS_OPTIONS.map(item => (
                                <Select.Option key={item} value={item}>
                                    {item}
                                </Select.Option>
                            ))}
                        </Select>
                        <span className={"cc-pf-grid__count"}>
                            {rows.length} case{rows.length === 1 ? "" : "s"}
                        </span>
                    </span>
                </div>
                <div className={"cc-pf-grid__actions"}>
                    <FiMovementBar
                        rows={this.props.rows}
                        selectedRows={this.state.selectedRows}
                        permissionContext={this.props.permissionContext}
                        recreditViewOnlyPermission={
                            this.props.recreditViewOnlyPermission
                        }
                        onDone={this.onFiMovementDone}
                    />
                </div>
                <Table
                    className={"cc-pf-antd"}
                    columns={columns}
                    dataSource={rows}
                    rowKey={(row, i) => row.caseId || i}
                    rowSelection={{
                        type: "checkbox",
                        selectedRowKeys: this.state.selectedRowKeys,
                        onChange: this.onSelectionChange,
                        getCheckboxProps: this.fiMovementCheckboxProps
                    }}
                    size={"small"}
                    bordered
                    pagination={false}
                    loading={this.props.loading}
                    scroll={{ x: totalX, y: 460 }}
                    locale={{
                        emptyText: (
                            <div className={"cc-portfolio-empty"}>
                                No linked cases
                            </div>
                        )
                    }}
                    sortDirections={["ascend", "descend", "ascend"]}
                />
            </div>
        );
    }
}

PortfolioCasesTable.propTypes = {
    rows: PropTypes.array,
    loading: PropTypes.bool,
    permissionContext: PropTypes.object,
    recreditViewOnlyPermission: PropTypes.bool,
    onFiMovementDone: PropTypes.func
};

export default PortfolioCasesTable;
